import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Wrench,
  Users,
  Truck,
  ShieldCheck,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  ArrowRight,
  AlertCircle,
  Wifi,
  WifiOff,
  Lock,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import LangSwitcher from "@/components/LangSwitcher";

// Trust points for the dark left panel
const TRUST_POINTS = [
  { Icon: Wrench, title: "Manage jobs and vehicles", body: "Job cards, inspections, tasks — from intake to delivery." },
  { Icon: Users, title: "Coordinate your team", body: "Advisors, mechanics, storekeepers and drivers in one place." },
  { Icon: Truck, title: "Track parts and delivery", body: "Inventory, pickup, drop and customer updates that flow." },
];

// Map backend HTTP status → friendly error state
const STATE_FROM_ERROR = (err) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail || "";
  if (!navigator.onLine) return { kind: "offline", title: "You're offline", body: "Login needs a connection. We'll keep your workspace ready when you're back." };
  if (status === 401 && /no account/i.test(detail)) return { kind: "no_account", title: "No account found", body: "We couldn't find an account with that email. Check the spelling or create a new workshop." };
  if (status === 401 && /password/i.test(detail)) return { kind: "wrong_password", title: "Wrong password", body: "That password doesn't match. Try again or reset your password." };
  if (status === 401 && /otp/i.test(detail)) return { kind: "otp_bad", title: "Incorrect OTP", body: detail };
  if (status === 403) return { kind: "suspended", title: "Account suspended", body: detail || "Ask your workshop owner to reactivate your account." };
  if (status === 429) return { kind: "locked", title: "Too many attempts", body: "Please wait a minute before trying again." };
  return { kind: "generic", title: "Sign-in failed", body: formatApiError(err) };
};

// Live online indicator
const useOnline = () => {
  const [on, setOn] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  useEffect(() => {
    const u = () => setOn(navigator.onLine);
    window.addEventListener("online", u);
    window.addEventListener("offline", u);
    return () => {
      window.removeEventListener("online", u);
      window.removeEventListener("offline", u);
    };
  }, []);
  return on;
};

export default function Login() {
  const { login, requestOtp, verifyOtp } = useAuth();
  const nav = useNavigate();
  const online = useOnline();

  // Mode: password | otp_email | otp_code
  const [mode, setMode] = useState("password");
  const [email, setEmail] = useState(() => localStorage.getItem("fender_last_email") || "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [otpEmail, setOtpEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = () => setError(null);

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    clearError();
    if (!online) {
      setError(STATE_FROM_ERROR({ response: { status: 0 } }));
      return;
    }
    setLoading(true);
    try {
      const data = await login(email.trim(), password);
      if (remember) localStorage.setItem("fender_last_email", email.trim());
      // 2FA gate for admin accounts
      if (data?.requires_2fa) {
        setOtpEmail(email.trim());
        setDemoOtp(data.demo_otp || "");
        setMode("otp_code");
        toast.info("Two-factor verification required.");
        return;
      }
      toast.success("Welcome back");
      const isAdmin = data?.user?.role === "sh_admin";
      nav(isAdmin ? "/admin/dashboard" : "/dashboard");
    } catch (err) {
      const st = STATE_FROM_ERROR(err);
      setError(st);
      toast.error(st.title);
    } finally {
      setLoading(false);
    }
  };

  const onRequestOtp = async (e) => {
    e.preventDefault();
    clearError();
    if (!online) {
      setError(STATE_FROM_ERROR({ response: { status: 0 } }));
      return;
    }
    setLoading(true);
    try {
      const data = await requestOtp(otpEmail.trim());
      setMode("otp_code");
      setDemoOtp(data?.demo_otp || "");
      toast.success(data?.message || "OTP sent");
    } catch (err) {
      const st = STATE_FROM_ERROR(err);
      setError(st);
      toast.error(st.title);
    } finally {
      setLoading(false);
    }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    clearError();
    setLoading(true);
    try {
      const data = await verifyOtp(otpEmail.trim(), otp.trim());
      localStorage.setItem("fender_last_email", otpEmail.trim());
      toast.success("Welcome back");
      const isAdmin = data?.user?.role === "sh_admin";
      nav(isAdmin ? "/admin/dashboard" : "/dashboard");
    } catch (err) {
      const st = STATE_FROM_ERROR(err);
      setError(st);
      toast.error(st.title);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex bg-white"
      style={{
        fontFamily:
          '"Noto Sans","Noto Sans Devanagari","Noto Sans Kannada","Noto Sans Tamil","Noto Sans Malayalam",Arial,sans-serif',
      }}
      data-testid="login-page"
    >
      {/* Left dark panel — trust and product statement */}
      <aside className="hidden lg:flex w-[46%] xl:w-[42%] bg-[#1d3557] text-white flex-col relative overflow-hidden">
        {/* subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.3) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative flex-1 p-10 xl:p-14 flex flex-col">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="login-brand-link">
            <img src="/fender-icon.png" alt="Fender" className="w-11 h-11" />
            <div className="leading-tight">
              <div className="font-bold text-[18px] text-white">Fender</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#a8dadc]">by SparesHub</div>
            </div>
          </Link>

          <div className="mt-14 max-w-md">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#e63946] mb-3">
              Workshop OS
            </div>
            <h2 className="text-[30px] xl:text-[36px] font-bold leading-[1.1] tracking-tight">
              Run your workshop with complete control.
            </h2>
            <p className="mt-3 text-[15px] text-[#a8dadc] leading-relaxed">
              World-class garage management for multi-brand workshops and mechanics.
            </p>
          </div>

          <ul className="mt-12 space-y-6 max-w-md">
            {TRUST_POINTS.map(({ Icon, title, body }) => (
              <li key={title} className="flex gap-4">
                <div className="w-9 h-9 shrink-0 rounded-lg bg-white/10 grid place-items-center text-[#a8dadc]">
                  <Icon size={18} strokeWidth={2} />
                </div>
                <div>
                  <div className="text-[15px] font-semibold">{title}</div>
                  <div className="text-[13px] text-[#a8dadc] leading-snug">{body}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-auto pt-10 flex items-center gap-4 text-[11px] text-[#a8dadc]">
            <div className="inline-flex items-center gap-1.5">
              <ShieldCheck size={13} /> Encrypted at rest
            </div>
            <span className="w-1 h-1 rounded-full bg-[#a8dadc]/50" />
            <div>Works offline · Syncs when back</div>
          </div>
        </div>
      </aside>

      {/* Right — login card */}
      <main className="flex-1 flex flex-col min-h-screen">
        {/* Top utility bar */}
        <div className="flex items-center justify-between px-4 md:px-10 pt-5">
          <Link to="/" className="lg:hidden inline-block" data-testid="login-mobile-logo">
            <img src="/fender-logo-lockup.png" alt="Fender by SparesHub" className="h-9 w-auto" />
          </Link>
          <div className="ml-auto flex items-center gap-2">
            {online ? (
              <div
                data-testid="login-online-pill"
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#12B76A] bg-[#12B76A]/10 border border-[#12B76A]/20 rounded-md px-2 py-1"
              >
                <Wifi size={12} /> Online
              </div>
            ) : (
              <div
                data-testid="login-offline-pill"
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#e63946] bg-[#e63946]/10 border border-[#e63946]/20 rounded-md px-2 py-1"
              >
                <WifiOff size={12} /> Offline
              </div>
            )}
            <LangSwitcher />
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-[420px]" data-testid="login-form-wrap">
            {mode === "password" && (
              <>
                <h1 className="text-[28px] md:text-[30px] font-bold text-[#1d3557] leading-tight">Sign in</h1>
                <p className="text-[14px] text-[#667085] mt-1">Welcome back — log in to your workspace.</p>
              </>
            )}
            {mode === "otp_email" && (
              <>
                <h1 className="text-[28px] md:text-[30px] font-bold text-[#1d3557] leading-tight">Login with OTP</h1>
                <p className="text-[14px] text-[#667085] mt-1">We&apos;ll send a one-time code to your email.</p>
              </>
            )}
            {mode === "otp_code" && (
              <>
                <h1 className="text-[28px] md:text-[30px] font-bold text-[#1d3557] leading-tight">Enter your code</h1>
                <p className="text-[14px] text-[#667085] mt-1">
                  Check your email for the 6-digit OTP sent to <strong>{otpEmail}</strong>.
                </p>
              </>
            )}

            {/* Error banner */}
            {error && (
              <div
                data-testid={`login-error-${error.kind}`}
                className="mt-5 flex gap-3 items-start p-3 rounded-lg border border-[#e63946]/25 bg-[#e63946]/5"
                role="alert"
              >
                <AlertCircle size={17} className="text-[#e63946] mt-0.5 shrink-0" />
                <div className="text-[13px]">
                  <div className="font-semibold text-[#e63946]">{error.title}</div>
                  <div className="text-[#667085] mt-0.5">{error.body}</div>
                </div>
              </div>
            )}

            {/* Demo OTP hint */}
            {mode === "otp_code" && demoOtp && (
              <div
                data-testid="login-demo-otp-hint"
                className="mt-5 flex gap-3 items-start p-3 rounded-lg border border-[#F79009]/25 bg-[#F79009]/5"
              >
                <KeyRound size={17} className="text-[#F79009] mt-0.5 shrink-0" />
                <div className="text-[13px]">
                  <div className="font-semibold text-[#F79009]">Demo mode</div>
                  <div className="text-[#667085] mt-0.5">
                    SMS not configured. Your OTP is <span className="font-mono font-bold text-[#1d3557]">{demoOtp}</span>.
                  </div>
                </div>
              </div>
            )}

            {/* Password form */}
            {mode === "password" && (
              <form onSubmit={onPasswordSubmit} className="mt-6 space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="login-email" className="text-[12px] font-semibold text-[#1d3557]">
                    Email
                  </Label>
                  <div className="relative mt-1.5">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085]" />
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearError();
                      }}
                      required
                      placeholder="you@garage.com"
                      className="pl-9 h-11 rounded-md bg-white border-[#E4E7EC] focus-visible:ring-[#e63946] focus-visible:border-[#e63946]"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password" className="text-[12px] font-semibold text-[#1d3557]">
                      Password
                    </Label>
                    <button
                      type="button"
                      onClick={() => nav("/forgot-password")}
                      data-testid="login-forgot-btn"
                      className="text-[12px] font-semibold text-[#457b9d] hover:text-[#e63946]"
                    >
                      Forgot?
                    </button>
                  </div>
                  <div className="relative mt-1.5">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085]" />
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type={showPw ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        clearError();
                      }}
                      required
                      placeholder="Your password"
                      className="pl-9 pr-10 h-11 rounded-md bg-white border-[#E4E7EC] focus-visible:ring-[#e63946] focus-visible:border-[#e63946]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#667085] hover:text-[#1d3557]"
                      data-testid="login-toggle-password"
                      aria-label={showPw ? "Hide password" : "Show password"}
                    >
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-[13px] text-[#1d3557] cursor-pointer select-none" htmlFor="login-remember">
                  <input
                    id="login-remember"
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 rounded border-[#E4E7EC] text-[#e63946]"
                    data-testid="login-remember"
                  />
                  Remember me on this device
                </label>

                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  disabled={loading}
                  className="w-full h-11 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
                >
                  {loading ? "Signing in…" : (<>Sign in <ArrowRight size={15} /></>)}
                </Button>

                <div className="relative py-3">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#E4E7EC]" />
                  </div>
                  <div className="relative flex justify-center text-[11px] text-[#667085] font-semibold uppercase tracking-wider">
                    <span className="bg-white px-3">or</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setMode("otp_email");
                    setOtpEmail(email);
                    clearError();
                  }}
                  data-testid="login-otp-link"
                  className="w-full h-11 rounded-md border border-[#E4E7EC] text-[#1d3557] font-semibold text-[13px] hover:border-[#457b9d] hover:bg-[#F1FAEE] transition-colors inline-flex items-center justify-center gap-2"
                >
                  <KeyRound size={14} />
                  Login with OTP
                </button>
              </form>
            )}

            {/* Request OTP form */}
            {mode === "otp_email" && (
              <form onSubmit={onRequestOtp} className="mt-6 space-y-4" data-testid="login-otp-request-form">
                <div>
                  <Label htmlFor="otp-email" className="text-[12px] font-semibold text-[#1d3557]">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085]" />
                    <Input
                      id="otp-email"
                      data-testid="login-otp-email-input"
                      type="email"
                      value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)}
                      required
                      placeholder="you@garage.com"
                      className="pl-9 h-11 rounded-md bg-white border-[#E4E7EC]"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="login-otp-request-btn"
                  disabled={loading}
                  className="w-full h-11 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
                >
                  {loading ? "Sending…" : "Send OTP"}
                </Button>
                <button
                  type="button"
                  onClick={() => setMode("password")}
                  data-testid="login-back-to-password"
                  className="w-full text-[13px] text-[#457b9d] hover:text-[#e63946] font-semibold inline-flex items-center justify-center gap-1"
                >
                  <ArrowLeft size={13} /> Back to password
                </button>
              </form>
            )}

            {/* Verify OTP form */}
            {mode === "otp_code" && (
              <form onSubmit={onVerifyOtp} className="mt-6 space-y-4" data-testid="login-otp-verify-form">
                <div>
                  <Label htmlFor="otp-code" className="text-[12px] font-semibold text-[#1d3557]">
                    6-digit code
                  </Label>
                  <Input
                    id="otp-code"
                    data-testid="login-otp-code-input"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/[^0-9]/g, ""));
                      clearError();
                    }}
                    required
                    placeholder="••••••"
                    className="mt-1.5 h-14 rounded-md bg-white border-[#E4E7EC] text-center text-[26px] font-mono tracking-[0.4em]"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="login-otp-verify-btn"
                  disabled={loading || otp.length < 6}
                  className="w-full h-11 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
                >
                  {loading ? "Verifying…" : "Verify & sign in"}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setMode("otp_email");
                    setOtp("");
                    setDemoOtp("");
                    clearError();
                  }}
                  data-testid="login-back-to-otp-email"
                  className="w-full text-[13px] text-[#457b9d] hover:text-[#e63946] font-semibold inline-flex items-center justify-center gap-1"
                >
                  <ArrowLeft size={13} /> Use a different email
                </button>
              </form>
            )}

            <p className="mt-8 text-[13px] text-[#667085] text-center">
              No workspace yet?{" "}
              <Link to="/register" data-testid="login-register-link" className="text-[#e63946] font-semibold hover:underline">
                Create Workshop Account
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-[#E4E7EC] py-4 px-6 text-center text-[11px] text-[#667085]">
          © Fender by SparesHub · <a href="mailto:contact@fenderapp.com" className="hover:text-[#e63946]">Support</a>
        </footer>
      </main>
    </div>
  );
}
